import fetch from 'isomorphic-fetch';

import {
  endAvatarUpload,
  startAvatarUpload,
  updateAvatars,
} from 'actions/avatar';
import { updateChecklist } from 'actions/checklist';
import { updateCompanyFeatureData } from 'actions/companyFeatures';
import { showConfirmationModal } from 'actions/confirmationModal';
import { closeErrorBar, showErrorBar } from 'actions/errorBar';
import { updateIntegrationSettings } from 'actions/integrations';
import { updateNotificationSettings } from 'actions/notification-settings';
import { onboardingUpdate, setSendingDownloadEmail } from 'actions/onboarding';
import { updateTriggers } from 'actions/triggers';
import {
  failedPatchUserInfo,
  getUserInfo,
  passwordValidation,
  patchingUserInfo,
  successPatchUserInfo,
  updateUserError,
  updateUserInfo,
  updateUserNotLoggedIn,
} from 'actions/user';
import {
  fetchJTBDFoldersVideos,
  updateJTBDContent,
  updateJTBDFoldersVideos,
} from 'actions/userJTBD';
import { updateWelcome, updateWelcomeHelpOptions } from 'actions/welcome';
import { FORBIDDEN_REMOVE_OAUTH_ERR } from 'constants/error-messages';
import { ERROR, SUCCESS } from 'constants/error-severities';
import {
  HEADER_CONTENT_TYPE,
  STATUS_FORBIDDEN,
  STATUS_NO_CONTENT,
} from 'constants/http';
import { JSON as JSON_MIME } from 'constants/mimes';
import { GOOGLE, INTERNAL, SLACK } from 'constants/oAuthKeys';
import { LOGOUT } from 'constants/routes';
import { INVITE_TEAM_VIEW_THRESHOLD } from 'constants/slack';
import { USER } from 'constants/ssr';
import { EDUCATION_VERIFIED_TAG, TEAM_ALPHA_TAG } from 'constants/tags';
import * as analytics from 'utilities/analytics';
import { clearRequestPushPermissionData } from 'utilities/localStorage';
import * as logger from 'utilities/loggerx';
import * as sentry from 'utilities/sentry';
import { inEmbedPlayer, isFromPublicSharePage } from 'utilities/url';
import { getMostCompleteNamePossible } from 'utilities/user';

const REMOVE_OAUTH_ROUTE = 'DELETE /v1/auth/oauth/';
const DEFAULT_REQUEST_OPTIONS = {
  credentials: 'include',
  headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
};

export function getUserCreator(withClearbit = false) {
  return async dispatch => {
    dispatch(getUserInfo());

    try {
      let userJSON = window[USER];

      if (withClearbit) {
        // tz_offset is the local timezone offset we use to keep the timezone for
        // the user in sync on the backend everytime we fetch the user.
        const url = `/v1/users?tz_offset=${new Date().getTimezoneOffset()}&clearbit=1`;
        const resp = await fetch(url, { credentials: 'include' });

        userJSON = resp.status < 400 && (await resp.json());
      }

      if (!userJSON) {
        dispatch(updateUserNotLoggedIn({}));

        return dispatch(updateUserError(Error('not logged in')));
      }

      const context = {};

      // do not pass page URL on public share page
      if (isFromPublicSharePage()) {
        context.page = { url: null };
      }

      sentry.setUserContext({ id: userJSON.id });

      // Segment will instantiate the Intercom messenger again if we identify -
      // we should simply not identify within the embed player
      if (!inEmbedPlayer()) {
        const userId = userJSON.is_sdk_shared_user ? null : userJSON.id;

        analytics.identify(
          userId,
          {
            email: userJSON.email,
            firstName: userJSON.first_name,
            lastName: userJSON.last_name,
            createdAt: userJSON.createdAt,
            loom_sign_up_date: userJSON.createdAt,
            dashboard_access: userJSON.dashboard_access,
            role: userJSON.role,
            company_name: userJSON.company_name,
            status: userJSON.status,
            num_credits: userJSON.num_credits,
            password_is_set: userJSON.password_is_set,
            accountType: userJSON.account_type,
            name: getMostCompleteNamePossible(userJSON),
            workspaceType: userJSON.workspaceType,
            team_alpha: Boolean(userJSON.tags && userJSON.tags[TEAM_ALPHA_TAG]),
            education_verified: Boolean(
              userJSON.tags && userJSON.tags[EDUCATION_VERIFIED_TAG]
            ),
          },
          { context }
        );

        try {
          analytics.activateProfitwell(userJSON.email);
        } catch (error) {
          sentry.logException(error, 'Error Activating Profitwell');
        }

        try {
          analytics.identifyUserleap(userJSON);
        } catch (error) {
          logger.error(
            error,
            { message: 'Error Identifying user for UserLeap' },
            { team: logger.LIFECYCLE_TEAM }
          );
        }
      }

      dispatch(updateChecklist(userJSON));

      const {
        avatars,
        clearbit,
        jtbd,
        persona,
        triggers,
        onboarding,
        company_feature_data: companyFeatureData,
        integration_settings: integrationSettings,
        notification_settings: notificationSettings,
        help_options: helpOptions,
      } = userJSON;

      const {
        clearbit_backfilled: clearbitBackfilled,
        clearbit_on_sign_up: clearbitOnSignUp,
        persona_v1: personaV1,
      } = persona;

      dispatch(updateNotificationSettings(notificationSettings));
      dispatch(updateIntegrationSettings(integrationSettings));
      dispatch(updateTriggers(triggers));
      dispatch(onboardingUpdate(onboarding));
      dispatch(
        updateWelcome({
          ...personaV1,
          clearbit,
          clearbitBackfilled,
          clearbitOnSignUp,
        })
      );
      dispatch(updateAvatars(avatars));
      dispatch(updateCompanyFeatureData(companyFeatureData));
      dispatch(updateWelcomeHelpOptions(helpOptions));

      if (personaV1.complete) {
        dispatch(updateJTBDContent(jtbd));
      }

      userJSON.has_named_video_views =
        userJSON.has_pro ||
        [SLACK, GOOGLE, INTERNAL].filter(
          channel =>
            userJSON.invite_counts[channel] >= INVITE_TEAM_VIEW_THRESHOLD
        ).length > 0;

      // update the user info last so all other reducers are filled when
      // is_logged_in and is_logged_in_checked are both true
      dispatch(updateUserInfo(userJSON));
    } catch (err) {
      console.error(err);

      return dispatch(updateUserError(err));
    }
  };
}

export function getJTBDCreator() {
  return (dispatch, getState) => {
    const { user, welcome } = getState();
    const { is_logged_in: isLoggedIn } = user;
    const { complete: welcomeComplete } = welcome;

    if (!isLoggedIn || !welcomeComplete) {
      return;
    }

    dispatch(fetchJTBDFoldersVideos());

    return fetch('/v1/users/jtbd', {
      credentials: 'include',
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw new Error(`GET jtbd - HTTP response: ${resp.status}`);
        }

        // do not try to parse empty string
        if (resp.status === STATUS_NO_CONTENT) {
          return {};
        }

        return resp.json();
      })
      .then(jtbdJSON => {
        const { folders, videos } = jtbdJSON;

        dispatch(updateJTBDFoldersVideos(folders, videos));
      })
      .catch(err => {
        sentry.logException(err, 'Error in GET to /v1/users/jtbd');
      });
  };
}

export function logoutUserCreator(redirectUrl = '/') {
  return () => {
    return fetch(LOGOUT, {
      method: 'POST',
      credentials: 'include',
    }).then(() => {
      // segment recommends cleaning up all cookies on logout
      // https://segment.com/docs/guides/best-practices/what-are-best-practices-for-identifying-users/
      analytics.clearCookies();

      // Clear localStorage key for notifications prompt in my video page
      clearRequestPushPermissionData();

      window.location.href = redirectUrl;
    });
  };
}

export function patchUserInfo(
  userId,
  userInfo,
  onSuccess = successPatchUserInfo
) {
  return dispatch => {
    if (!userId) {
      return;
    }

    dispatch(patchingUserInfo());

    return fetchRequest({
      url: `/v1/users/${userId}`,
      options: {
        method: 'PATCH',
        body: JSON.stringify(userInfo),
      },
    })
      .then(userJSON => {
        dispatch(updateUserInfo(userJSON));
        dispatch(onSuccess());
      })
      .catch(() => {
        dispatch(failedPatchUserInfo());
        dispatch(showErrorBar(ERROR, 'Failed to update user account'));
      });
  };
}

export function validateCurrentPassword(password) {
  return dispatch => {
    if (password.length === 0) {
      return dispatch(passwordValidation(null));
    }

    return fetchRequest({
      url: '/v1/auth/verify_password',
      options: {
        method: 'POST',
        body: JSON.stringify({ password }),
      },
    })
      .then(responseJSON => {
        dispatch(passwordValidation(responseJSON.valid));
      })
      .catch(() => {
        dispatch(showErrorBar(ERROR, 'Failed to update user account'));
      });
  };
}

export function removeOAuth(provider) {
  return dispatch => {
    if (provider == null) {
      return;
    }

    return fetch(`/v1/auth/oauth/${provider}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
    })
      .then(response => {
        if (response.status === STATUS_FORBIDDEN) {
          throw new Error(FORBIDDEN_REMOVE_OAUTH_ERR);
        }

        if (response.status >= 400) {
          throw new Error(`got a bad response status: ${response.status}`);
        }

        dispatch(getUserCreator(true));
      })
      .catch(error => {
        if (error.message === FORBIDDEN_REMOVE_OAUTH_ERR) {
          dispatch(showErrorBar(ERROR, error.message));

          return;
        }

        dispatch(
          showErrorBar(ERROR, `Failed to disconnect ${provider} account`)
        );
        sentry.logException(error, `Error in ${REMOVE_OAUTH_ROUTE}${provider}`);
      });
  };
}

export function postVerifyEmail() {
  return dispatch => {
    return fetchRequest({
      url: '/api/users/verify-email',
      options: {
        method: 'POST',
        headers: {},
      },
    })
      .then(() => {
        dispatch(showErrorBar(SUCCESS, 'Verification email has been resent.'));
      })
      .catch(() => {
        dispatch(
          showErrorBar(ERROR, 'Oops! Failed to re-send email verification.')
        );
      });
  };
}

export function postDownloadLoomEmail() {
  return dispatch => {
    dispatch(setSendingDownloadEmail(true));

    return fetchRequest({
      url: '/v1/users/send-loom-links',
      options: {
        method: 'POST',
        headers: {},
      },
    })
      .then(() => {
        dispatch(setSendingDownloadEmail(false));
        dispatch(showErrorBar(SUCCESS, 'Successfully sent download email'));
      })
      .catch(() => {
        dispatch(setSendingDownloadEmail(false));
        dispatch(showErrorBar(ERROR, 'Oops! Failed to send download email'));
      });
  };
}

export function postExistingUserDownloadLoomEmail() {
  return dispatch => {
    dispatch(setSendingDownloadEmail(true));

    return fetchRequest({
      url: '/v1/users/send-download-reminder',
      options: {
        method: 'POST',
        headers: {},
      },
    })
      .then(() => {
        dispatch(setSendingDownloadEmail(false));
        dispatch(showErrorBar(SUCCESS, 'Successfully sent reminder email'));
      })
      .catch(() => {
        dispatch(setSendingDownloadEmail(false));
        dispatch(showErrorBar(ERROR, 'Oops! Failed to send reminder email'));
      });
  };
}

export function patchAvatarUpdate(avatars) {
  return dispatch => {
    dispatch(updateAvatars(avatars));

    return fetchRequest({
      url: '/v1/users/avatar',
      options: {
        method: 'PATCH',
        body: JSON.stringify({ avatars }),
      },
    })
      .then(userJSON => {
        dispatch(updateUserInfo(userJSON));
        dispatch(updateAvatars(userJSON.avatars));
      })
      .catch(() => {
        dispatch(showErrorBar(ERROR, 'Oops! Failed to update avatars.'));
      });
  };
}

export function patchAvatarUpload(file) {
  return dispatch => {
    dispatch(startAvatarUpload());

    const formData = new FormData();

    formData.append('file', file);

    return fetch('/v1/users/avatar/upload', {
      method: 'PATCH',
      credentials: 'include',
      body: formData,
    })
      .then(response => {
        dispatch(showErrorBar(SUCCESS, 'Avatar uploaded successfully.'));
        window.setTimeout(() => dispatch(closeErrorBar()), 3000);

        return response.json();
      })
      .then(userJSON => {
        dispatch(endAvatarUpload());
        dispatch(updateUserInfo(userJSON));
        dispatch(updateAvatars(userJSON.avatars));
      })
      .catch(() => {
        dispatch(showErrorBar(ERROR, 'Oops! Failed to update avatars.'));
        dispatch(endAvatarUpload());
      });
  };
}

export function patchDeleteAvatar(filename) {
  return dispatch => {
    return fetchRequest({
      url: '/v1/users/avatar/delete',
      options: {
        method: 'PATCH',
        body: JSON.stringify({ filename }),
      },
    })
      .then(userJSON => {
        dispatch(updateUserInfo(userJSON));
        dispatch(updateAvatars(userJSON.avatars));
      })
      .catch(() => {
        dispatch(showErrorBar(ERROR, 'Oops! Failed to delete avatar.'));
      });
  };
}
export function patchCompanyFeaturesInfo(fields) {
  return dispatch => {
    dispatch(updateCompanyFeatureData(fields));

    return fetchRequest({
      url: '/v1/users/company',
      options: {
        method: 'PATCH',
        body: JSON.stringify({ company_feature_data: fields }),
      },
    })
      .then(userJSON => {
        dispatch(updateUserInfo(userJSON));
        dispatch(updateCompanyFeatureData(userJSON.company_feature_data));
      })
      .catch(() => {
        dispatch(
          showErrorBar(ERROR, 'Oops! Failed to update company features.')
        );
      });
  };
}

export function patchUserVideoSettings(video_settings) {
  return dispatch => {
    return fetchRequest({
      url: '/v1/users/video_settings',
      options: {
        method: 'PATCH',
        body: JSON.stringify({ video_settings }),
      },
    })
      .then(userJSON => {
        dispatch(updateUserInfo(userJSON));
      })
      .then(() =>
        dispatch(showConfirmationModal('Default video settings saved'))
      )
      .catch(() =>
        dispatch(
          showConfirmationModal(
            'Oops! Failed to update default video settings.',
            ERROR
          )
        )
      );
  };
}

export function fetchRequest(settings = {}) {
  const options = { ...DEFAULT_REQUEST_OPTIONS, ...settings.options };

  return fetch(settings.url, options)
    .then(response => {
      if (response.status >= 400) {
        throw new Error(`got a bad response status: ${response.status}`);
      }

      // not a JSON request
      if (options.headers[HEADER_CONTENT_TYPE] !== JSON_MIME) {
        return response;
      }

      return response.json();
    })
    .catch(error => {
      sentry.logException(
        error,
        `Error in ${options.method} to ${settings.url}`
      );
      throw new Error(error);
    });
}
